import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles/EvCharging.module.scss';

// Context.
import { OmaLummeApp, EvChargingApp } from '../../omaLummeContext';

// Components.
import Box from '../Common/Box';
import Loading from '../Common/Loading';

// Actions.
import { fetchContract } from '../../actions';
// Types.
import { Contract, ContractBasicData } from '../../types/contract';
// Utils.
import { formatDate } from '../../utils/billing';
import { contractIsEvCharging, formatTag } from '../../utils/evCharging';

const ContractsAndTags: React.FunctionComponent = () => {
  const { t } = useTranslation();

  const { users, basicData } = OmaLummeApp.useState();
  const { evChargerInfo } = EvChargingApp.useState();

  const [evContracts, setEvContracts] = React.useState<Contract[]>();
  const [evContractsLoading, setEvContractsLoading] = React.useState<boolean>(false);

  React.useEffect(() => {
    async function fetchEvContracts(): Promise<void> {
      const evChargingContracts = basicData ? basicData.filter((d) => contractIsEvCharging(d)) : undefined;
      if (evChargingContracts && evChargingContracts?.length > 0) {
        setEvContractsLoading(true);

        const promises: Promise<Contract | false>[] = [];
        evChargingContracts.forEach((cbd: ContractBasicData) => {
          promises.push(fetchContract(parseInt(cbd.customerNo), users.activeUser.userId, cbd.contractNo));
        });

        const contracts = await Promise.all(promises).then((arrOfResults) => {
          const results: Contract[] = [];
          arrOfResults.flat().forEach((d) => {
            if (d) {
              results.push(d);
            }
          });

          return results;
        });

        setEvContracts(contracts);
        setEvContractsLoading(false);
      }
    }
    fetchEvContracts();
  }, [basicData]);

  return (
    <div>
      <header className={`${styles.evChargingHeader} ${styles.mainHeader}`}>
        <h1 aria-live="polite">{t(`evCharging:contractsAndTags.title`)}</h1>
      </header>

      {!evChargerInfo && <Box>{t(`evCharging:common.noEvContract`)}</Box>}
      {evChargerInfo && (
        <>
          <Box size="twoThirds">
            <h2>{t(`evCharging:contractsAndTags.driver`)}</h2>
            {users.activeUser.userName && (
              <div className={styles.detail}>
                <h4>{users.activeUser.userName}</h4>
              </div>
            )}

            {users.activeUser.userNumber && (
              <div className={styles.detail}>
                <h4>{t(`common:general.phone`)}</h4>
                {users.activeUser.userNumber}
              </div>
            )}

            {users.activeUser.userEmail && (
              <div className={styles.detail}>
                <h4>{t(`common:general.email`)}</h4>
                {users.activeUser.userEmail}
              </div>
            )}

            {evChargerInfo.sites.length > 0 && (
              <div className={styles.detail}>
                <h4>{t(`evCharging:common.site`)}</h4>
                {evChargerInfo.sites.map((site) => (
                  <span key={site.groupId}>{site.groupName}</span>
                ))}
              </div>
            )}
          </Box>
          {evContractsLoading && <Loading />}
          {evContracts?.map((c) => {
            const tag = evChargerInfo.tags.find((tag) => tag.idTag === c.rfid);
            return (
              <Box key={c.contractCode} size="twoThirds">
                <h2>{t(`common:general.contract`)}</h2>

                <div className={styles.detail}>
                  <h4>{t(`common:general.contractNumber`)}</h4>
                  {c.contractCode}
                </div>
                {c.contractStatus && (
                  <div className={styles.detail}>
                    <h4>{t(`evCharging:contractsAndTags.contractStatus`)}</h4>
                    {t(`evCharging:contractsAndTags.status${c.contractStatus.contractStatusNo}`)}
                  </div>
                )}
                <div className={styles.detail}>
                  <h4>{t(`contracts:contracts.startDate`)}</h4>
                  {formatDate(c.startDate)}
                </div>
                <div className={styles.detail}>
                  <h4>{t(`contracts:contracts.endDate`)}</h4>
                  {c.endDate ? formatDate(c.endDate) : t(`contracts:contracts.ongoing`)}
                </div>

                <div className={styles.detail}>
                  <h4>{t(`evCharging:common.tag`)}</h4>

                  {tag && (
                    <span>
                      {formatTag(tag.idTag)} ({tag.tokenName})
                    </span>
                  )}
                </div>
              </Box>
            );
          })}
        </>
      )}
    </div>
  );
};

export default ContractsAndTags;
